import { checkFormDefinition, type Reason } from '../../format/check';
import { findElement, parentOf } from '../../format/elements';
import type { FormDefinition } from '../../format/form-definition';
import { AUTHOR_TEXTS } from '../../texts/author';
import { elementName } from '../../texts/words';
import { insertElement, moveElement, removeElement } from './tree';
import { changed, EditFunctions, EditResult, refused } from './editContracts';
import { InsertionPosition, landingOf, landingsByStep, mayLand } from '../elementInsertion';
import { CopyIds, copyOf } from './copy';
import { placesOf } from '../../format/references';

export type ArrangingEdit =
  | { kind: 'moveElement'; elementId: string; to: InsertionPosition }
  | { kind: 'moveElementByStep'; elementId: string; direction: 'up' | 'down' }
  | { kind: 'duplicateElement'; elementId: string; ids: CopyIds }
  | { kind: 'deleteElement'; elementId: string };

export const arrangingEdits: EditFunctions<ArrangingEdit> = {
  moveElement: (form, { elementId, to }) => move(form, elementId, to),

  moveElementByStep: (form, { elementId, direction }) => {
    const now = landingOf(form, elementId);
    const containersNow = new Set(now ? containersAround(form, now) : []);
    let refusal: EditResult | undefined;
    for (const to of landingsByStep(form, elementId, direction)) {
      const result = move(form, elementId, to);
      if (result.kind !== 'refused') return result;
      refusal ??= result;
      const entering = containersAround(form, to).some(id => !containersNow.has(id));
      if (!entering) return result;
    }
    return refusal ?? changed(form);
  },

  duplicateElement: (form, { elementId, ids }) => {
    const element = findElement(form, elementId);
    const at = landingOf(form, elementId);
    if (!element || !at) return changed(form);
    return changed(insertElement(form, copyOf(element, ids), { parentId: at.parentId, index: at.index + 1 }));
  },

  deleteElement: (form, { elementId }) => deleteElement(form, elementId),
};

const move = (form: FormDefinition, elementId: string, to: InsertionPosition): EditResult => {
  const element = findElement(form, elementId);
  if (!element || !mayLand(form, element, to)) return changed(form);
  const next = moveElement(form, elementId, to);
  if (next === form) return changed(form);
  const broken = checkFormDefinition(next)[0];
  return broken ? refused(cantMove(next, broken)) : changed(next);
};

const containersAround = (form: FormDefinition, at: InsertionPosition): string[] => {
  const ids: string[] = [];
  for (let id = at.parentId; id !== null; id = parentOf(form, id)?.id ?? null) ids.push(id);
  return ids;
};

const cantMove = (next: FormDefinition, reason: Reason): string => {
  const { elementId, reference } = reason;
  const from = elementId === undefined ? undefined : findElement(next, elementId);
  const to = reference && findElement(next, reference.toId);
  if (from && to && reference) {
    const [dependent, depended] = [elementName(from), elementName(to)];
    switch (reference.problem) {
      case 'below':
        return AUTHOR_TEXTS.arranging.stayBelow(dependent, depended);
      case 'inside':
        return AUTHOR_TEXTS.arranging.notInside(dependent, depended);
      case 'container':
        return AUTHOR_TEXTS.arranging.notInsideContainer(dependent, depended);
      case 'intoGroup':
        return AUTHOR_TEXTS.arranging.notIntoGroup(dependent, depended);
    }
  }
  const field = from?.kind === 'field' ? from : undefined;
  const unique = field?.validations?.some(validation => validation.kind === 'unique');
  if (field && unique && !placesOf(next).get(field.id)?.group)
    return AUTHOR_TEXTS.arranging.uniqueOutsideGroup(elementName(field));
  return AUTHOR_TEXTS.arranging.other(reason.text);
};

const deleteElement = (form: FormDefinition, elementId: string): EditResult => {
  const element = findElement(form, elementId);
  if (!element) return changed(form);
  return changed(removeElement(form, elementId));
};
